import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  UsersIcon,
  Eye,
  Key,
  UserPlus,
  Loader2,
  Search,
  RefreshCw,
  Download,
  FileJson,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { userService } from '@/services/user.service';
import { homeDetailsService } from '@/services/homeDetailsService';

interface User {
  id: number;
  cidade: string;
  codigo: number;
  cpf_cnpj: string;
  email: string;
  telefone: string;
  estado: string;
  nome_razao_social: string;
  role: string;
  coordenadora_id: number;
  coordenadora_nome?: string;
  indicadora_id?: number;
  indicadora_nome?: string;
  created_at?: string;
  data_indicacao?: string;
  total_compras?: number;
  total_vendas?: number;
}

interface BigNumber {
  label: string;
  value: number;
  bgColor: string;
  icon: React.ReactNode;
}

const Users = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  
  // Estados de filtro
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('nome_razao_social');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  
  // Informações de paginação da API
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Estados de ações
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedUserForCoord, setSelectedUserForCoord] = useState<User | null>(null);
  const [showCoordDialog, setShowCoordDialog] = useState(false);
  const [selectedCoordenadora, setSelectedCoordenadora] = useState('');
  const [coordenadoras, setCoordanadoras] = useState<any[]>([]);
  
  // Filtro por coordenadora
  const [coordFilter, setCoordFilter] = useState('');

  // Filtro por role
  const [roleFilter, setRoleFilter] = useState('');

  // Confirmação de reset de senha
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [userForReset, setUserForReset] = useState<User | null>(null);

  // Big numbers
  const [bigNumbers, setBigNumbers] = useState<BigNumber[]>([]);

  const roles = ['admin', 'coordenadora', 'revendedora', 'cliente'];

  const roleColors: { [key: string]: string } = {
    admin: 'bg-red-100 text-red-800',
    coordenadora: 'bg-blue-100 text-blue-800',
    revendedora: 'bg-yellow-100 text-yellow-800',
    cliente: 'bg-green-100 text-green-800',
  };

  const roleDisplayNames: { [key: string]: string } = {
    admin: 'Administrator',
    coordenadora: 'Coordenadora',
    revendedora: 'Revendedora',
    cliente: 'Cliente',
  };

  const statusDisplayNames: { [key: string]: string } = {
    'ativo': 'Ativo',
    'inativo': 'Inativo',
  };

  useEffect(() => {
    const userDataString = localStorage.getItem('userlogged');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUser(userData);

      if (userData.role !== 'admin') {
        toast.error('Acesso restrito');
        navigate('/home');
        return;
      }

      loadData();
    } else {
      navigate('/authentication/side-login');
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [statusFilter, coordFilter, roleFilter, page, pageSize, sortBy, sortOrder]);

  // Debounce para search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (user?.role === 'admin') {
        setPage(1); // Resetar para primeira página ao buscar
        loadData();
      }
    }, 1500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search]);

  const loadData = async () => {
    setLoadingUsers(true);
    try {
      const [usersResponse, coordData, homeDetails] = await Promise.all([
        userService.getAllUsers(page, pageSize, search, sortBy, sortOrder, statusFilter, coordFilter ? Number(coordFilter) : 0, roleFilter),
        userService.getCoordenadoras(),
        homeDetailsService.findAll(),
      ]);

      // Extrair dados da resposta estruturada
      const users = usersResponse?.data || [];
      const pagination = usersResponse?.pagination || {};

      setAllUsers(users);
      setTotalUsers(pagination.total || 0);
      setTotalPages(pagination.totalPages || 0);
      setCoordanadoras(coordData || []);
      calculateBigNumbers(homeDetails, pagination.total || 0);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoadingUsers(false);
      setLoading(false);
    }
  };

  const calculateBigNumbers = (homeDetails: any, total: number) => {
    const coordenadoras = homeDetails?.qtdCoordenadoras || 0;
    const revendedoras = homeDetails?.qtdRevendedora || 0;
    const clientes = homeDetails?.qtdClientes || 0;
    
    // Calcular administradores diminuindo do total
    const adminCount = Math.max(0, total - (coordenadoras + revendedoras + clientes));

    setBigNumbers([
      {
        label: 'Administradores',
        value: adminCount,
        bgColor: 'bg-red-500',
        icon: <UsersIcon className="w-8 h-8" />,
      },
      {
        label: 'Coordenadoras',
        value: coordenadoras,
        bgColor: 'bg-blue-500',
        icon: <UsersIcon className="w-8 h-8" />,
      },
      {
        label: 'Revendedoras',
        value: revendedoras,
        bgColor: 'bg-yellow-500',
        icon: <UsersIcon className="w-8 h-8" />,
      },
      {
        label: 'Clientes',
        value: clientes,
        bgColor: 'bg-green-500',
        icon: <UsersIcon className="w-8 h-8" />,
      },
    ]);
  };

  const handleViewUser = (userId: number) => {
    window.open(`/v2/usuarios/${userId}`, '_blank');
  };

  const handleRelacionarCoordenadora = async (selectedUser: User) => {
    if (!selectedCoordenadora) {
      toast.error('Selecione uma coordenadora');
      return;
    }

    setLoadingAction(true);
    try {
      await userService.atribuirCoordenadora(
        parseInt(selectedCoordenadora),
        selectedUser.id
      );
      toast.success('Coordenadora atribuída com sucesso');
      setShowCoordDialog(false);
      setSelectedCoordenadora('');
      await loadData();
    } catch (error) {
      console.error('Erro ao atribuir coordenadora:', error);
      toast.error('Erro ao atribuir coordenadora');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userForReset) return;

    setLoadingAction(true);
    try {
      await userService.resetUserPassword(userForReset.id);
      toast.success('Senha resetada com sucesso');
      setResetConfirmOpen(false);
      setUserForReset(null);
      await loadData();
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      toast.error('Erro ao resetar senha');
    } finally {
      setLoadingAction(false);
    }
  };

  const exportToJSON = () => {
    const dataToExport = allUsers.map(u => ({
      codigo: u.codigo,
      nome: u.nome_razao_social,
      email: u.email,
      cpf_cnpj: u.cpf_cnpj,
      role: u.role,
      coordenadora: u.coordenadora_nome || '--',
      cidade: u.cidade,
      estado: u.estado,
    }));

    const jsonString = JSON.stringify(dataToExport, null, 2);
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/json;charset=utf-8,' + encodeURIComponent(jsonString)
    );
    element.setAttribute('download', 'usuarios.json');
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const exportToCSV = () => {
    const dataToExport = allUsers.map(u => [
      u.codigo,
      u.nome_razao_social,
      u.email,
      u.cpf_cnpj,
      u.role,
      u.coordenadora_nome || '--',
      u.cidade,
      u.estado,
    ]);

    const headers = [
      ['Código', 'Nome', 'Email', 'CPF/CNPJ', 'Função', 'Coordenadora', 'Cidade', 'Estado'],
      ...dataToExport,
    ];

    const csvContent = headers.map(row => row.join(',')).join('\n');
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent)
    );
    element.setAttribute('download', 'usuarios.csv');
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  if (loading) {
    return (
      <div className="w-full space-y-6 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="h-20 bg-gray-200 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="h-96 bg-gray-200 rounded animate-pulse" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-6">
      {/* Big Numbers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {bigNumbers.map((bigNum, index) => (
          <Card key={index} className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    {bigNum.label}
                  </p>
                  <p className={`text-3xl font-bold ${bigNum.bgColor.replace('bg-', 'text-')}`}>
                    {bigNum.value}
                  </p>
                </div>
                <div className={`${bigNum.bgColor} p-3 rounded-lg text-white`}>
                  {bigNum.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Card Principal */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Gerenciar Usuários</CardTitle>
              <CardDescription>
                Visualize, gerencie e organize os usuários
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => loadData()}
              disabled={loading}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          {/* Filtros e Buscas */}
          <div className="space-y-3 mb-6">
            {/* Busca - linha própria */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Busque por nome, email, CPF/CNPJ, código ou telefone..."
                className="pl-10 pr-10"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  title="Limpar busca"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Dropdowns de filtro - mesma linha */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select value={statusFilter} onValueChange={(value) => {
                setStatusFilter(value == "all" ? '' : value);
                setPage(1);
              }}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filtrar por status..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>

              <Select value={coordFilter} onValueChange={(value) => {
                setCoordFilter(value === 'all' ? '' : value);
                setPage(1);
              }}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filtrar por coordenadora..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as coordenadoras</SelectItem>
                  {coordenadoras.map(coord => (
                    <SelectItem key={coord.id} value={coord.id.toString()}>
                      {coord.nome_razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={roleFilter} onValueChange={(value) => {
                setRoleFilter(value === 'all' ? '' : value);
                setPage(1);
              }}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filtrar por função..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as funções</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="coordenadora">Coordenadora</SelectItem>
                  <SelectItem value="revendedora">Revendedora</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Exportar */}
            <div className="flex gap-2 justify-between items-center">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportToCSV}
                  disabled={allUsers.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportToJSON}
                  disabled={allUsers.length === 0}
                >
                  <FileJson className="w-4 h-4 mr-2" />
                  JSON
                </Button>
              </div>
              <p className="text-sm text-gray-600">
                Total: {totalUsers} usuários
              </p>
            </div>
          </div>

          {/* Tabela */}
          <div className="overflow-x-auto relative">
            {loadingUsers && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-lg z-10">
                <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b">
                  <TableHead className="font-semibold">
                    Nome<br/>
                    <span className="text-xs text-gray-500">Indicação</span>
                  </TableHead>
                  <TableHead className="font-semibold">
                    Email<br/>
                    <span className="text-xs text-gray-500">Telefone</span>
                  </TableHead>
                  <TableHead className="font-semibold">
                    Função<br/>
                    <span className="text-xs text-gray-500">Ativo</span>
                  </TableHead>
                  <TableHead className="font-semibold">
                    Coordenadora
                    <br/>
                    <span className="text-xs text-gray-500">ou Revendedora</span>
                  </TableHead>
                  <TableHead className="font-semibold">Cidade/UF</TableHead>
                  <TableHead className="font-semibold text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allUsers.length > 0 ? (
                  allUsers.map(user => (
                    <TableRow key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={
                      ()=>{
                        handleViewUser(user.id);
                      }}
                      onMouseDown={(e) => {
                        if (e.button === 1) {
                          e.preventDefault();
                          handleViewUser(user.id);
                        }
                      }}>
                       <TableCell >
                        <span>#{user.codigo} - {user.nome_razao_social}</span><br/>
                        {user.indicadora_id ? (
                            <span 
                              onClick={(e) => { e.stopPropagation(); handleViewUser(user.indicadora_id); }}
                               className='text-xs text-muted-foreground cursor-pointer max-w-[160px] truncate'>
                                Indicação: {user.indicadora_nome} - {user.data_indicacao ? new Date(user.data_indicacao).toLocaleDateString() : '--'}
                            </span>
                        ) : (
                            <span className='text-xs text-muted-foreground/40'>Sem indicação</span>
                        )}
                      </TableCell>
                      <TableCell  className="text-sm text-gray-600">
                       {user.email}<br/>
                       {user.telefone}
                      </TableCell>
                      <TableCell>
                        <Badge className={roleColors[user.role]}>
                          {roleDisplayNames[user.role]}
                        </Badge>
                        <br/>
                        {(user.role === 'cliente' ? user.total_compras >= 100 : user.total_vendas >= 500) ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            Ativo
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800 border-gray-200">
                            Inativo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {user.coordenadora_nome || '--'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {user.cidade} - {user.estado}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleViewUser(user.id); }}
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {user.role === 'revendedora' && user.coordenadora_id === 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); setSelectedUserForCoord(user); setShowCoordDialog(true); }}
                              title="Atribuir coordenadora"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); setUserForReset(user); setResetConfirmOpen(true); }}
                            title="Resetar senha"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-600 py-8">
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          {totalUsers > 0 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Mostrando {(page - 1) * pageSize + 1} a{' '}
                  {Math.min(page * pageSize, totalUsers)} de{' '}
                  {totalUsers}
                </span>
              </div>
              <div className="flex gap-2 items-center">
                <Select value={pageSize.toString()} onValueChange={(value) => {
                  setPageSize(parseInt(value));
                  setPage(1);
                }}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="75">75</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <div className="flex items-center gap-2">
                  {/* Primeiras 3 páginas */}
                  {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map(pageNum => (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={page === pageNum ? 'default' : 'outline'}
                      onClick={() => setPage(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  ))}
                  
                  {/* Reticências e Select se houver muitas páginas */}
                  {totalPages > 6 && (
                    <>
                      <span className="text-gray-400">•••</span>
                      <Select value={page.toString()} onValueChange={(value) => setPage(parseInt(value))}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                            <SelectItem key={pageNum} value={pageNum.toString()}>
                              Página {pageNum}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-gray-400">•••</span>
                    </>
                  )}

                  {/* Últimas 3 páginas */}
                  {totalPages > 3 && Array.from({ length: Math.min(3, totalPages) }, (_, i) => totalPages - 2 + i).map(pageNum => 
                    pageNum > 3 ? (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={page === pageNum ? 'default' : 'outline'}
                        onClick={() => setPage(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    ) : null
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Próximo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para atribuir coordenadora */}
      <Dialog open={showCoordDialog} onOpenChange={setShowCoordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atribuir Coordenadora</DialogTitle>
            <DialogDescription>
              Selecione a coordenadora para {selectedUserForCoord?.nome_razao_social}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Select value={selectedCoordenadora} onValueChange={setSelectedCoordenadora}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione uma coordenadora..." />
              </SelectTrigger>
              <SelectContent>
                {coordenadoras.map(coord => (
                  <SelectItem key={coord.id} value={coord.id.toString()}>
                    {coord.nome_razao_social}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCoordDialog(false);
                  setSelectedCoordenadora('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleRelacionarCoordenadora(selectedUserForCoord!)}
                disabled={loadingAction || !selectedCoordenadora}
              >
                {loadingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Atribuir'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alert para resetar senha */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogTitle>Resetar Senha</AlertDialogTitle>
          <AlertDialogDescription>
            Você deseja resetar a senha de {userForReset?.nome_razao_social}? Esta ação não pode
            ser desfeita.
          </AlertDialogDescription>
          <div className="flex gap-2 justify-end">
            <AlertDialogCancel disabled={loadingAction}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetPassword}
              disabled={loadingAction}
              className="bg-red-600 hover:bg-red-700"
            >
              {loadingAction ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetando...
                </>
              ) : (
                'Resetar Senha'
              )}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
